/** TypeScript interfaces for fault analysis results. */

// =========================================================================
// Fault Study
// =========================================================================

export interface NodeFaultCurrent {
  node_name: string
  voltage_kv: number | null
  distance_from_source_ft: number
  fault_current_3ph_ka: number
  fault_current_slg_ka: number | null
  x_r_ratio_at_fault: number | null
  asymmetric_peak_ka: number | null
  asymmetric_rms_ka: number | null
  impedance_z1_real_ohm: number | null
  impedance_z1_imag_ohm: number | null
  impedance_z0_real_ohm: number | null
  impedance_z0_imag_ohm: number | null
  arc_flash_energy_cal_cm2: number | null
  arc_flash_category: string | null
  clearing_time_sec: number | null
  clearing_time_source: string | null
  source_reachable: boolean
}

export interface FaultStudySummary {
  max_fault_3ph_ka: number
  min_fault_3ph_ka: number
  max_fault_slg_ka: number | null
  min_fault_slg_ka: number | null
  total_nodes: number
  nodes_above_8ka: number
  connected_components: number
  islanded_segment_count: number
}

export interface FaultStudyResult {
  feeder_id: number
  feeder_name: string | null
  substation_name: string | null
  source_fault_current_ka: number | null
  nominal_voltage_kv: number | null
  analysis_timestamp: string | null
  source_data_provenance: string | null
  indicative_only: boolean
  summary: FaultStudySummary
  nodes: NodeFaultCurrent[]
  warnings: string[]
  /** A3 (spec §3.5): modelling assumptions this run applied. Optional so
   *  fixtures/payloads predating the field still type-check. */
  assumptions?: string[]
}

// =========================================================================
// Device Duty
// =========================================================================

export interface DeviceDutyResult {
  device_id: number
  device_name: string | null
  device_type: string
  gis_id: string | null
  node_name: string | null
  fault_current_ka: number
  fault_current_type: string
  interrupting_rating_ka: number | null
  duty_ratio_pct: number | null
  status: 'adequate' | 'marginal' | 'inadequate' | 'unknown'
}

export interface DeviceDutyReport {
  feeder_id: number
  feeder_name: string | null
  analysis_timestamp: string | null
  source_data_provenance: string | null
  indicative_only: boolean
  total_devices: number
  adequate_count: number
  marginal_count: number
  inadequate_count: number
  unknown_count: number
  devices: DeviceDutyResult[]
  warnings: string[]
  /** A3 (spec §3.5): modelling assumptions this run applied. */
  assumptions?: string[]
}

// =========================================================================
// Arc Flash
// =========================================================================

export interface ArcFlashResult {
  node_name: string
  device_id: number | null
  device_name: string | null
  device_type: string | null
  bolted_fault_current_ka: number
  arcing_current_ka: number
  incident_energy_cal_cm2: number
  arc_flash_boundary_mm: number | null
  ppe_category: string
  clearing_time_sec: number
  clearing_time_source: string
  working_distance_mm: number
  enclosure_type: string
}

export interface ArcFlashReport {
  feeder_id: number
  feeder_name: string | null
  analysis_timestamp: string | null
  methodology: string
  source_data_provenance: string | null
  indicative_only: boolean
  total_locations: number
  dangerous_count: number
  cat4_count: number
  cat3_count: number
  cat2_count: number
  cat1_count: number
  no_ppe_count: number
  estimated_clearing_time_count: number
  results: ArcFlashResult[]
  warnings: string[]
  disclaimers: string[]
  /** A3 (spec §3.5): modelling assumptions this run applied. */
  assumptions?: string[]
}

// =========================================================================
// Protection Coordination
// =========================================================================

export interface ProtectionCoordinationPair {
  upstream_device_id: number
  upstream_device_name: string | null
  upstream_device_type: string
  upstream_rating: string | null
  downstream_device_id: number
  downstream_device_name: string | null
  downstream_device_type: string
  downstream_rating: string | null
  coordination_status: string
  notes: string | null

  // --- Phase 3: curve-based evaluation detail (all optional; null pre-eval). ---
  fault_current_ka?: number | null
  coordination_basis?: string | null
  mm_upstream_s?: number | null
  tc_downstream_s?: number | null
  melt_ratio?: number | null
}

export interface ProtectionCoordinationResult {
  feeder_id: number
  feeder_name: string | null
  analysis_timestamp: string | null
  total_devices: number
  coordinated_pairs: number
  flagged_pairs: number
  tcc_review_needed: number
  pairs: ProtectionCoordinationPair[]
  warnings: string[]

  // --- Phase 3: provenance stamp (mirrors DeviceDutyReport). ---
  source_data_provenance?: string | null
  indicative_only?: boolean
  cannot_assess_pairs?: number
  /** A3 (spec §3.5): modelling assumptions this run applied. */
  assumptions?: string[]
}

// =========================================================================
// Color Maps
// =========================================================================

export const DUTY_STATUS_COLORS: Record<string, string> = {
  adequate: '#2ecc71',
  marginal: '#f1c40f',
  inadequate: '#e74c3c',
  unknown: '#95a5a6',
}

export const PPE_CATEGORY_COLORS: Record<string, string> = {
  'No PPE Required': '#2ecc71',
  'Cat 1': '#3498db',
  'Cat 2': '#f1c40f',
  'Cat 3': '#e67e22',
  'Cat 4': '#e74c3c',
  'DANGEROUS': '#8e44ad',
}

export const COORDINATION_COLORS: Record<string, string> = {
  coordinated: '#2ecc71',
  marginal: '#f1c40f',
  uncoordinated: '#e74c3c',
  requires_tcc_review: '#3498db',
}

// =========================================================================
// Device Rating Coverage (spec §5 item 2)
// =========================================================================

export interface DeviceRatingCoverageItem {
  device_type: string
  rated: number
  parseable: number
  missing: number
  unplaced: number
  total: number
}

/** One rating-coverage worklist row (C2 CSV export): a device sitting in a
 *  non-rated bucket, identified well enough for GIS/field follow-up. */
export interface UnratedDeviceItem {
  device_id: number
  device_name: string | null
  device_type: string
  gis_id: string | null
  bucket: 'parseable' | 'missing' | 'unplaced'
  rating: string | null
}

export interface DeviceRatingCoverageReport {
  feeder_id: number
  feeder_name: string | null
  analysis_timestamp: string | null
  total_devices: number
  rated: number
  parseable: number
  missing: number
  unplaced: number
  items: DeviceRatingCoverageItem[]
  warnings: string[]
  worklist: UnratedDeviceItem[]
}

export const COVERAGE_BUCKET_COLORS: Record<string, string> = {
  rated: '#2ecc71',
  parseable: '#3498db',
  missing: '#e74c3c',
  unplaced: '#95a5a6',
}

// =========================================================================
// Persisted snapshot (§item 5 latest-vs-live compare)
// =========================================================================

export interface FaultStudySnapshotNode {
  node_name: string
  voltage_kv: number | null
  distance_from_source_ft: number | null
  fault_current_3ph_ka: number
  fault_current_slg_ka: number | null
  x_r_ratio_at_fault: number | null
  asymmetric_peak_ka: number | null
  asymmetric_rms_ka: number | null
  analysis_timestamp: string | null
}

export interface FaultStudySnapshot {
  feeder_id: number
  analysis_timestamp: string | null
  node_count: number
  nodes: FaultStudySnapshotNode[]
}

// =========================================================================
// Combined study (§item 1 compute-once)
// =========================================================================

export interface CombinedFaultStudyResult {
  feeder_id: number
  study: FaultStudyResult
  duty: DeviceDutyReport
  arc_flash: ArcFlashReport
}
